import type {Schedule} from '../../types/domain'

export type SegmentClock =
  | {kind: 'running'; deadlineMs: number}
  | {kind: 'paused'; remainingMs: number}
  | {kind: 'suddenDeath'}

export interface TimerSnapshot {
  hasStarted?: boolean
  levelIndex: number
  breakIndex: number
  onBreak: boolean
  capturedAtMs: number
  clock: SegmentClock
}

export interface SessionSnapshot {
  schemaVersion: 2
  sessionId: string
  ownerUid: string
  createdAtMs: number
  revision: number
  schedule: Schedule
  timer: TimerSnapshot
}

export type HostingStatus = 'recovering' | 'hosting' | 'viewOnly' | 'legacy'

export interface EffectiveTimer {
  playing: boolean
  remainingMs: number | null
  missedBoundary: boolean
}

export function projectTimer(snapshot: TimerSnapshot, serverNowMs: number): EffectiveTimer {
  switch (snapshot.clock.kind) {
    case 'suddenDeath':
      return {playing: false, remainingMs: null, missedBoundary: false}
    case 'paused':
      return {playing: false, remainingMs: snapshot.clock.remainingMs, missedBoundary: false}
    case 'running': {
      const remainingMs = Math.max(0, snapshot.clock.deadlineMs - serverNowMs)
      return {playing: remainingMs > 0, remainingMs, missedBoundary: remainingMs === 0}
    }
  }
}

export interface ServerTimeAnchor {
  serverAtReceiptMs: number
  monotonicAtReceiptMs: number
}

export function anchorServerTime(serverTimeMs: number, sentAtMs: number, receivedAtMs: number): ServerTimeAnchor {
  if (![serverTimeMs, sentAtMs, receivedAtMs].every(Number.isFinite) || receivedAtMs < sentAtMs) {
    throw new Error('Invalid server-time sample')
  }
  return {serverAtReceiptMs: serverTimeMs + receivedAtMs - sentAtMs, monotonicAtReceiptMs: receivedAtMs}
}

export function serverTimeAt(anchor: ServerTimeAnchor, monotonicNowMs: number): number {
  return anchor.serverAtReceiptMs + Math.max(0, monotonicNowMs - anchor.monotonicAtReceiptMs)
}

export function restoreTimer(snapshot: TimerSnapshot, serverNowMs: number): TimerSnapshot {
  const effective = projectTimer(snapshot, serverNowMs)
  if (snapshot.clock.kind !== 'running' || !effective.missedBoundary) return snapshot
  return {...snapshot, capturedAtMs: serverNowMs, clock: {kind: 'paused', remainingMs: 0}}
}
